import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { questionPaperAPI, qpFormatsAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Home, Sparkles, Save, Download, Wand2, Upload, X, Loader2, FileText, Trash2, Languages, Bookmark, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import CurriculumPicker from '@/components/CurriculumPicker';

const CBSE_BOARDS = ['CBSE', 'ICSE', 'State Board', 'IB', 'Cambridge', 'Other'];
const EXAM_TYPES = ['Sample Paper', 'Unit Test', 'Half Yearly', 'Pre-Board', 'Board Exam', 'Final Exam'];
const LANGS = [
  { v: 'en', l: 'English' },
  { v: 'hi', l: 'हिन्दी Hindi' },
  { v: 'sa', l: 'संस्कृत Sanskrit' },
  { v: 'fr', l: 'Français French' },
  { v: 'es', l: 'Español Spanish' },
  { v: 'de', l: 'Deutsch German' },
  { v: 'zh', l: '中文 Mandarin' },
];

const QuestionPaperBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [patterns, setPatterns] = useState([]);
  const [form, setForm] = useState({
    title: 'CBSE Question Paper',
    subject: '', class_name: '', chapter: '', board: 'CBSE', book_name: '',
    exam_type: 'Sample Paper', max_marks: 80, duration_minutes: 180,
    pattern_key: '', general_instructions: [], sections: [], language: 'en',
    is_draft: true, custom_school_name: '',
  });
  const [chapterText, setChapterText] = useState('');
  const [chapterFilename, setChapterFilename] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saved, setSaved] = useState(null); // saved qp with id
  const [showSavePattern, setShowSavePattern] = useState(false);
  const [savingPattern, setSavingPattern] = useState(false);
  const [patternLabel, setPatternLabel] = useState('');
  const [patternShared, setPatternShared] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [libScope, setLibScope] = useState({ book_ids: [], chapter_ids: [], topic_ids: [], scope_summary: '', filters: {} });
  const [qpFormats, setQpFormats] = useState({ admin_templates: [], my_formats: [] });
  const [selectedFormatId, setSelectedFormatId] = useState('');
  const [difficulty, setDifficulty] = useState('mixed');
  const [uploadingFormat, setUploadingFormat] = useState(false);
  const [showFormatUpload, setShowFormatUpload] = useState(false);
  const [newFormat, setNewFormat] = useState({ title: '', is_admin_template: false, file: null });
  const formatFileRef = useRef(null);
  const chapterFileRef = useRef(null);
  const API_BLOB_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    questionPaperAPI.patterns().then(({ data }) => setPatterns(data.patterns || [])).catch(() => {});
  }, []);

  const loadFormats = useCallback(() => {
    const params = {};
    if (form.board) params.board = form.board;
    if (form.class_name) params.class_name = form.class_name;
    if (form.subject) params.subject = form.subject;
    qpFormatsAPI.list(params).then(({ data }) => setQpFormats(data || { admin_templates: [], my_formats: [] })).catch(() => {});
  }, [form.board, form.class_name, form.subject]);

  useEffect(() => { loadFormats(); }, [loadFormats]);

  const handleUploadFormat = async () => {
    if (!form.board) { toast.error('Pick a Board first (in the form above)'); return; }
    if (!form.class_name) { toast.error('Pick a Class first (in the form above)'); return; }
    if (!newFormat.title.trim()) { toast.error('Give this format a Title'); return; }
    if (!newFormat.file) { toast.error('Choose a PDF / DOCX / TXT file'); return; }
    setUploadingFormat(true);
    try {
      const { data } = await qpFormatsAPI.upload(newFormat.file, {
        title: newFormat.title, board: form.board, class_name: form.class_name,
        subject: form.subject || '', is_admin_template: newFormat.is_admin_template && user?.role === 'admin',
      });
      toast.success(`Format saved — ${(data.sections || []).length} sections detected`);
      setShowFormatUpload(false);
      setNewFormat({ title: '', is_admin_template: false, file: null });
      loadFormats();
      setSelectedFormatId(data.format_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploadingFormat(false); }
  };

  const handleDeleteFormat = async (fid) => {
    if (!window.confirm('Delete this format?')) return;
    try {
      await qpFormatsAPI.delete(fid);
      toast.success('Deleted');
      if (selectedFormatId === fid) setSelectedFormatId('');
      loadFormats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  useEffect(() => {
    if (id) {
      questionPaperAPI.getOne(id).then(({ data }) => {
        setForm({ ...form, ...data });
        setSaved(data);
      }).catch(() => toast.error('Failed to load paper'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-suggest pattern
  useEffect(() => {
    if (form.class_name && form.subject && !form.pattern_key) {
      questionPaperAPI.suggest(form.class_name, form.subject).then(({ data }) => {
        if (data.pattern_key && data.pattern) {
          setForm((f) => ({
            ...f, pattern_key: data.pattern_key,
            max_marks: data.pattern.max_marks || f.max_marks,
            duration_minutes: data.pattern.duration_minutes || f.duration_minutes,
            general_instructions: data.pattern.general_instructions || [],
          }));
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.class_name, form.subject]);

  const handlePatternChange = async (key) => {
    if (!key) return;
    setForm({ ...form, pattern_key: key });
    try {
      const { data } = await questionPaperAPI.pattern(key);
      setForm((f) => ({
        ...f, pattern_key: key,
        subject: data.subject || f.subject,
        class_name: data.class_name || f.class_name,
        max_marks: data.max_marks || f.max_marks,
        duration_minutes: data.duration_minutes || f.duration_minutes,
        general_instructions: data.general_instructions || [],
      }));
    } catch { toast.error('Pattern load failed'); }
  };

  const handleUploadChapter = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await questionPaperAPI.uploadChapter(file);
      setChapterText(data.chapter_text || '');
      setChapterFilename(file.name);
      toast.success(`Extracted ${data.chapter_text?.length || 0} characters`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      if (chapterFileRef.current) chapterFileRef.current.value = '';
    }
  };

  const handleGenerate = async () => {
    if (!form.subject || !form.class_name) {
      toast.error('Fill Class and Subject first'); return;
    }
    setGenerating(true);
    try {
      const { data } = await questionPaperAPI.generate({
        subject: form.subject, class_name: form.class_name, chapter: form.chapter || libScope.scope_summary || '',
        board: form.board, book_name: form.book_name, exam_type: form.exam_type,
        max_marks: parseInt(form.max_marks) || 80, duration_minutes: parseInt(form.duration_minutes) || 180,
        pattern_key: form.pattern_key || null,
        chapter_text: chapterText || null, language: form.language,
        library_book_ids: libScope.book_ids,
        library_chapter_ids: libScope.chapter_ids,
        library_topic_ids: libScope.topic_ids,
        qp_format_id: selectedFormatId || null,
        difficulty: difficulty || null,
      });
      setForm((f) => ({
        ...f, sections: data.sections || [], general_instructions: data.general_instructions || f.general_instructions,
        max_marks: data.max_marks || f.max_marks, duration_minutes: data.duration_minutes || f.duration_minutes,
      }));
      toast.success(`Question paper generated!${data.curriculum_scope_summary ? ' (scope-locked)' : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleSave = async (finalize = false) => {
    if (!user?.school_id) { toast.error('Complete your profile first'); navigate('/profile'); return; }
    setSaving(true);
    const payload = { ...form, is_draft: !finalize };
    try {
      let result;
      if (saved?.question_paper_id) {
        const { data } = await questionPaperAPI.update(saved.question_paper_id, payload);
        result = data;
      } else {
        const { data } = await questionPaperAPI.create(payload);
        result = data;
        navigate(`/question-paper/${data.question_paper_id}`, { replace: true });
      }
      setSaved(result);
      toast.success(finalize ? 'Question paper published!' : 'Draft saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDownload = async (fmt) => {
    if (!saved?.question_paper_id) { toast.error('Save the paper first'); return; }
    setDownloading(fmt);
    try {
      const api = fmt === 'docx' ? questionPaperAPI.downloadDocx : questionPaperAPI.downloadPdf;
      const { data } = await api(saved.question_paper_id, false);
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url;
      const safe = (form.title || 'question-paper').replace(/[^a-z0-9-_ ]/gi, '').slice(0, 40).trim();
      a.download = `${safe}.${fmt}`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error('Download failed'); }
    finally { setDownloading(''); }
  };

  const handleSavePattern = async () => {
    if (!form.sections.length) { toast.error('Generate a paper first'); return; }
    if (!patternLabel.trim()) { toast.error('Give this pattern a name'); return; }
    setSavingPattern(true);
    try {
      const sections = form.sections.map((s) => ({
        name: s.name, description: s.description || '', instructions: s.instructions || '',
        marks_per_question: s.marks_per_question || 1,
        count: s.questions?.length || 1,
        question_type: s.questions?.[0]?.type || 'short',
      }));
      await questionPaperAPI.createMyPattern({
        label: patternLabel.trim(), class_name: form.class_name || '', subject: form.subject || '',
        max_marks: parseInt(form.max_marks) || 80, duration_minutes: parseInt(form.duration_minutes) || 180,
        general_instructions: form.general_instructions || [],
        sections,
        is_shared: patternShared,
      });
      toast.success('Pattern saved to My Patterns');
      setShowSavePattern(false); setPatternLabel(''); setPatternShared(false);
      // Refresh pattern list
      const { data } = await questionPaperAPI.patterns();
      setPatterns(data.patterns || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save pattern');
    } finally { setSavingPattern(false); }
  };

  const handleTranslate = async (target) => {
    if (!saved?.question_paper_id) { toast.error('Save the paper first'); return; }
    setTranslating(true);
    try {
      const { data } = await questionPaperAPI.translate(saved.question_paper_id, target);
      toast.success(`${target.toUpperCase()} twin created — opening now`);
      navigate(`/question-paper/${data.question_paper_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Translation failed');
    } finally { setTranslating(false); }
  };

  const totalQuestions = form.sections.reduce((n, s) => n + (s.questions?.length || 0), 0);
  const inlinePdfUrl = saved?.question_paper_id
    ? `${API_BLOB_URL}/api/question-papers/${saved.question_paper_id}/pdf/inline?include_answer_key=false`
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50/30">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-orange-200/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button data-testid="qp-back-btn" variant="ghost" onClick={() => navigate('/dashboard')} className="text-orange-700"><Home className="w-5 h-5 mr-2" /> Home</Button>
          <div className="flex items-center gap-2"><FileText className="w-6 h-6 text-orange-600" /><h1 className="text-xl font-bold text-[#1C1917]">Question Paper Generator</h1></div>
          <div className="flex gap-2">
            <Button data-testid="qp-save-draft-btn" variant="outline" onClick={() => handleSave(false)} disabled={saving} className="border border-orange-200">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button data-testid="qp-publish-btn" onClick={() => handleSave(true)} disabled={saving || form.sections.length === 0} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white">
              Publish
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <CurriculumPicker onChange={setLibScope} defaultClass={form.class_name} defaultSubject={form.subject} defaultBoard={form.board || 'CBSE'} />
        <Card className="p-6 border border-orange-200/60 bg-white shadow-sm">
          <h2 className="text-lg font-bold text-orange-800 mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5" /> Paper Setup</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Title</Label>
              <Input data-testid="qp-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Class</Label>
              <Input data-testid="qp-class-input" value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} placeholder="10" className="mt-1" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input data-testid="qp-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Mathematics" className="mt-1" />
            </div>
            <div>
              <Label>Board</Label>
              <Select value={form.board} onValueChange={(v) => setForm({ ...form, board: v })}>
                <SelectTrigger data-testid="qp-board-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CBSE_BOARDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Textbook / Book Name</Label>
              <Input data-testid="qp-book-input" value={form.book_name} onChange={(e) => setForm({ ...form, book_name: e.target.value })} placeholder="NCERT" className="mt-1" />
            </div>
            <div>
              <Label>Exam Type</Label>
              <Select value={form.exam_type} onValueChange={(v) => setForm({ ...form, exam_type: v })}>
                <SelectTrigger data-testid="qp-exam-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{EXAM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Topic / Chapter Scope</Label>
              <Input data-testid="qp-chapter-input" value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} placeholder={libScope.chapter_ids?.length ? `${libScope.chapter_ids.length} chapter(s) picked from Library` : 'e.g. Real Numbers, Full Syllabus'} className="mt-1" autoComplete="off" />
              {libScope.chapter_ids?.length > 0 && <p className="text-xs text-emerald-700 mt-1" data-testid="qp-curriculum-hint">📚 {libScope.chapter_ids.length} chapter(s) selected from Curriculum Library</p>}
            </div>
            <div>
              <Label>Max Marks</Label>
              <Input data-testid="qp-max-marks-input" type="number" min="10" max="200" value={form.max_marks} onChange={(e) => setForm({ ...form, max_marks: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Duration (minutes)</Label>
              <Input data-testid="qp-duration-input" type="number" min="30" max="360" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>CBSE Pattern (auto-suggested — you can change)</Label>
              <Select value={form.pattern_key} onValueChange={handlePatternChange}>
                <SelectTrigger data-testid="qp-pattern-select" className="mt-1"><SelectValue placeholder="Select CBSE SQP pattern..." /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {patterns.map((p) => <SelectItem key={p.key} value={p.key}>{p.label} — {p.max_marks} marks</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger data-testid="qp-language-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Difficulty</Label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger data-testid="qp-difficulty-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy · Recall-first</SelectItem>
                  <SelectItem value="medium">Medium · Balanced</SelectItem>
                  <SelectItem value="hard">Hard · HOTS-heavy</SelectItem>
                  <SelectItem value="mixed">Mixed · CBSE default</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="p-6 border border-orange-200/60 bg-white shadow-sm" data-testid="qp-format-library-card">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-orange-800 flex items-center gap-2"><FileText className="w-5 h-5" /> Question Paper Format Library</h2>
              <p className="text-sm text-stone-600">Pick an admin-pinned format for your board+class, or upload your own PDF/DOCX blueprint. The AI will use it as the paper structure.</p>
            </div>
            <Button data-testid="qp-upload-format-btn" variant="outline" onClick={() => setShowFormatUpload(true)} className="border border-orange-200">
              <Upload className="w-4 h-4 mr-2" /> Upload New Format
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(qpFormats.admin_templates || []).length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-orange-700">📌 Admin-pinned templates</Label>
                <div className="mt-1 space-y-2 max-h-56 overflow-y-auto pr-1">
                  {(qpFormats.admin_templates || []).map((f) => (
                    <div key={f.format_id} data-testid={`qp-format-admin-${f.format_id}`}
                         onClick={() => setSelectedFormatId(f.format_id === selectedFormatId ? '' : f.format_id)}
                         className={`p-3 rounded-md border cursor-pointer transition ${selectedFormatId === f.format_id ? 'bg-orange-50 border-orange-400 ring-2 ring-orange-300' : 'bg-white border-stone-200 hover:border-orange-300'}`}>
                      <div className="text-sm font-semibold text-stone-800">{f.title}</div>
                      <div className="text-xs text-stone-500">
                        {f.board} · Class {f.class_name}{f.subject ? ` · ${f.subject}` : ''} · {(f.sections || []).length} sections{f.max_marks ? ` · ${f.max_marks} marks` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wide text-orange-700">👤 My uploads</Label>
              <div className="mt-1 space-y-2 max-h-56 overflow-y-auto pr-1">
                {(qpFormats.my_formats || []).length === 0 && (
                  <div className="text-xs text-stone-500 italic p-2">No personal formats yet. Upload a PDF/DOCX to reuse.</div>
                )}
                {(qpFormats.my_formats || []).map((f) => (
                  <div key={f.format_id} data-testid={`qp-format-mine-${f.format_id}`}
                       onClick={() => setSelectedFormatId(f.format_id === selectedFormatId ? '' : f.format_id)}
                       className={`p-3 rounded-md border cursor-pointer transition group ${selectedFormatId === f.format_id ? 'bg-amber-50 border-amber-400 ring-2 ring-amber-300' : 'bg-white border-stone-200 hover:border-amber-300'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-stone-800 truncate">{f.title}</div>
                        <div className="text-xs text-stone-500 truncate">
                          {f.board} · Class {f.class_name}{f.subject ? ` · ${f.subject}` : ''} · {(f.sections || []).length} sections{f.max_marks ? ` · ${f.max_marks} marks` : ''}
                        </div>
                      </div>
                      <button data-testid={`qp-format-delete-${f.format_id}`}
                              onClick={(e) => { e.stopPropagation(); handleDeleteFormat(f.format_id); }}
                              className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600 transition">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {selectedFormatId && (
            <div className="mt-3 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-800" data-testid="qp-format-selected-note">
              ✅ Using uploaded format — the AI will strictly follow this paper's sections + marks distribution. CBSE Pattern selection above will be ignored.
            </div>
          )}
        </Card>

        <Card className="p-6 border border-orange-200/60 bg-white shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-orange-800 flex items-center gap-2"><Upload className="w-5 h-5" /> Ground the AI (optional)</h2>
            {chapterFilename && (
              <button data-testid="qp-clear-chapter" onClick={() => { setChapterText(''); setChapterFilename(''); }} className="text-xs text-stone-500 hover:text-red-600">Clear</button>
            )}
          </div>
          <p className="text-sm text-stone-600 mb-3">Upload the NCERT chapter (PDF/DOCX/TXT) so AI writes questions strictly from it. If skipped, AI uses NCERT knowledge + internet.</p>
          <input ref={chapterFileRef} data-testid="qp-chapter-file-input" type="file" accept=".pdf,.docx,.txt" onChange={handleUploadChapter} className="hidden" />
          <Button data-testid="qp-upload-chapter-btn" variant="outline" onClick={() => chapterFileRef.current?.click()} className="border border-orange-200">
            <Upload className="w-4 h-4 mr-2" /> {chapterFilename || 'Upload Chapter (PDF / DOCX / TXT)'}
          </Button>
          {chapterText && <p className="text-xs text-emerald-700 mt-2">{chapterText.length.toLocaleString()} characters grounded</p>}
        </Card>

        <div className="flex justify-center">
          <Button data-testid="qp-generate-btn" onClick={handleGenerate} disabled={generating || !form.subject || !form.class_name} className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white text-base px-8 py-6 shadow-lg">
            <Wand2 className={`w-5 h-5 mr-2 ${generating ? 'animate-spin' : ''}`} /> {generating ? 'Generating (this may take 30-60s)...' : 'Generate Question Paper with AI'}
          </Button>
        </div>

        {form.sections.length > 0 && (
          <Card className="p-6 border border-orange-200/60 bg-white shadow-sm" data-testid="qp-preview-card">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-lg font-bold text-orange-800 flex items-center gap-2"><Edit3 className="w-5 h-5" /> Generated Paper — click any field to edit</h2>
                <p className="text-sm text-stone-600">{form.sections.length} sections · {totalQuestions} questions · {form.max_marks} marks · {form.duration_minutes} min</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {saved?.question_paper_id && (
                  <>
                    <Button data-testid="qp-preview-btn" variant="outline" onClick={() => setShowPreview(true)} className="border border-orange-200"><FileText className="w-4 h-4 mr-2" /> Preview PDF</Button>
                    <Button data-testid="qp-save-pattern-btn" variant="outline" onClick={() => setShowSavePattern(true)} className="border border-orange-200">
                      <Bookmark className="w-4 h-4 mr-2" /> Save as My Pattern
                    </Button>
                    <Button data-testid="qp-translate-hindi-btn" variant="outline" onClick={() => handleTranslate('hi')} disabled={translating || form.language === 'hi'} className="border border-orange-200">
                      {translating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Languages className="w-4 h-4 mr-2" />} Hindi Twin
                    </Button>
                    <Select onValueChange={(v) => handleTranslate(v)} disabled={translating}>
                      <SelectTrigger data-testid="qp-translate-any-select" className="w-40 border-orange-200 h-10">
                        <SelectValue placeholder="Translate to…" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGS.filter((l) => l.v !== form.language).map((l) => (
                          <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
                <Button data-testid="qp-download-docx-btn" variant="outline" onClick={() => handleDownload('docx')} disabled={!saved || downloading === 'docx'} className="border border-orange-200">
                  {downloading === 'docx' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} DOCX
                </Button>
                <Button data-testid="qp-download-pdf-btn" variant="outline" onClick={() => handleDownload('pdf')} disabled={!saved || downloading === 'pdf'} className="border border-orange-200">
                  {downloading === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} PDF
                </Button>
              </div>
            </div>
            {!saved && <p className="text-xs text-amber-700 mb-3 bg-amber-50 border border-amber-200 rounded px-3 py-2">Click "Save Draft" first to enable downloads.</p>}
            <p className="text-xs text-emerald-700 mb-4 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">✏️ Edit any question, option, sub-part or answer inline. Then "Save Draft" and download as DOCX or PDF.</p>
            <div className="space-y-6">
              {form.sections.map((sec, sIdx) => (
                <div key={sIdx} className="border-l-4 border-orange-500 pl-4">
                  <div className="flex justify-between items-baseline mb-2">
                    <h3 className="font-bold text-orange-700 text-base">{sec.name}</h3>
                    <span className="text-xs text-stone-500">{sec.description} · {sec.marks_per_question}M each</span>
                  </div>
                  {sec.instructions && <p className="text-xs italic text-stone-600 mb-2">{sec.instructions}</p>}
                  <div className="space-y-3">
                    {(sec.questions || []).map((q, qIdx) => {
                      const updateQ = (patch) => {
                        setForm((f) => {
                          const newSecs = [...f.sections];
                          const newQs = [...(newSecs[sIdx].questions || [])];
                          newQs[qIdx] = { ...newQs[qIdx], ...patch };
                          newSecs[sIdx] = { ...newSecs[sIdx], questions: newQs };
                          return { ...f, sections: newSecs };
                        });
                      };
                      return (
                      <div key={qIdx} className="text-sm bg-stone-50 rounded p-3 border border-stone-200" data-testid={`qp-question-${sIdx}-${qIdx}`}>
                        <div className="flex justify-between font-medium mb-1">
                          <span>Q{q.number}. {q.stem ? <em className="text-orange-700">[Case Study]</em> : ''}</span>
                          <span className="text-orange-700 text-xs">[{q.marks || sec.marks_per_question} marks]</span>
                        </div>
                        {q.stem !== undefined && q.stem !== null && (
                          <Textarea data-testid={`qp-stem-${sIdx}-${qIdx}`} value={q.stem} onChange={(e) => updateQ({ stem: e.target.value })} rows={2} className="mb-1 text-xs bg-orange-50 border-orange-200" />
                        )}
                        <Textarea data-testid={`qp-qtext-${sIdx}-${qIdx}`} value={q.question || ''} onChange={(e) => updateQ({ question: e.target.value })} rows={2} className="text-sm border-stone-300" />
                        {q.options && (
                          <ul className="mt-1 ml-4 text-stone-700 space-y-0.5">
                            {q.options.map((o, i) => (
                              <li key={i}><Input value={o} onChange={(e) => { const nOpts = [...q.options]; nOpts[i] = e.target.value; updateQ({ options: nOpts }); }} className="text-xs h-7 border-stone-200" data-testid={`qp-opt-${sIdx}-${qIdx}-${i}`} /></li>
                            ))}
                          </ul>
                        )}
                        {q.sub_parts && (
                          <div className="mt-2 space-y-1 ml-3">
                            {q.sub_parts.map((sp, i) => (
                              <div key={i} className="flex gap-2 items-center">
                                <strong className="text-xs w-8">{sp.label}</strong>
                                <Input value={sp.question || ''} onChange={(e) => { const nSubs = [...q.sub_parts]; nSubs[i] = { ...sp, question: e.target.value }; updateQ({ sub_parts: nSubs }); }} className="text-xs h-7 border-stone-200 flex-1" data-testid={`qp-sub-${sIdx}-${qIdx}-${i}`} />
                                <span className="text-stone-500 text-xs whitespace-nowrap">({sp.marks}M)</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="mt-2">
                          <label className="text-xs text-stone-600 flex items-start gap-1">
                            <span className="font-semibold whitespace-nowrap">Answer:</span>
                            <Textarea data-testid={`qp-answer-${sIdx}-${qIdx}`} value={q.answer || ''} onChange={(e) => updateQ({ answer: e.target.value })} rows={1} className="text-xs h-8 border-stone-200 flex-1" />
                          </label>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent data-testid="qp-preview-modal" className="max-w-6xl h-[90vh] p-0">
          <DialogHeader className="p-4 border-b border-stone-200 flex-row items-center justify-between">
            <DialogTitle>{form.title} — {form.subject} · Class {form.class_name}</DialogTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}><X className="w-4 h-4" /></Button>
          </DialogHeader>
          {inlinePdfUrl && <iframe title="QP Preview" src={inlinePdfUrl} className="w-full h-full border-0" />}
        </DialogContent>
      </Dialog>

      <Dialog open={showSavePattern} onOpenChange={setShowSavePattern}>
        <DialogContent data-testid="qp-save-pattern-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save Current Sections as Pattern</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-4">
            <div>
              <Label>Pattern Name</Label>
              <Input data-testid="qp-pattern-label-input" value={patternLabel} onChange={(e) => setPatternLabel(e.target.value)} placeholder="e.g. My Class 10 Unit Test" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Class</Label>
                <Input data-testid="qp-pattern-class-input" value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} placeholder="10" className="mt-1" />
              </div>
              <div>
                <Label>Subject</Label>
                <Input data-testid="qp-pattern-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Mathematics" className="mt-1" />
              </div>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              Class & Subject let this pattern auto-suggest when someone types those fields later.
            </p>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="qp-pattern-shared-check" checked={patternShared} onChange={(e) => setPatternShared(e.target.checked)} className="accent-orange-500" />
              <span>Share with everyone at my school</span>
            </label>
            <p className="text-xs text-stone-500">This saves the section structure only (names, counts, marks). AI will regenerate fresh questions each reuse.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowSavePattern(false)} className="border border-stone-200">Cancel</Button>
            <Button data-testid="qp-save-pattern-confirm-btn" onClick={handleSavePattern} disabled={savingPattern || !patternLabel.trim()} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white">
              {savingPattern ? 'Saving…' : 'Save Pattern'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload New Format Dialog */}
      <Dialog open={showFormatUpload} onOpenChange={setShowFormatUpload}>
        <DialogContent className="max-w-md" data-testid="qp-format-upload-dialog">
          <DialogHeader><DialogTitle>Upload Question Paper Format</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs text-stone-600">
              Upload a sample paper (PDF / DOCX) — the AI will parse it into sections & marks and re-use the structure for all your generated papers on <b>{form.board || '—'}</b> · Class <b>{form.class_name || '—'}</b>{form.subject ? <> · <b>{form.subject}</b></> : null}.
            </p>
            <div>
              <Label>Format Title</Label>
              <Input data-testid="qp-new-format-title" value={newFormat.title}
                     onChange={(e) => setNewFormat({ ...newFormat, title: e.target.value })}
                     placeholder="e.g. Half-Yearly Blueprint 2025-26" className="mt-1" />
            </div>
            <div>
              <Label>Sample Paper File (PDF / DOCX / TXT · max 50 MB)</Label>
              <input ref={formatFileRef} data-testid="qp-new-format-file" type="file" accept=".pdf,.docx,.txt"
                     onChange={(e) => setNewFormat({ ...newFormat, file: e.target.files?.[0] || null })}
                     className="mt-1 block w-full text-sm text-stone-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:bg-orange-100 file:text-orange-800 hover:file:bg-orange-200" />
              {newFormat.file && <p className="text-xs text-emerald-700 mt-1">{newFormat.file.name} · {(newFormat.file.size / 1024).toFixed(1)} KB</p>}
            </div>
            {user?.role === 'admin' && (
              <label className="flex items-center gap-2 text-sm text-stone-700" data-testid="qp-new-format-admin-toggle-label">
                <input type="checkbox" checked={newFormat.is_admin_template}
                       onChange={(e) => setNewFormat({ ...newFormat, is_admin_template: e.target.checked })}
                       data-testid="qp-new-format-admin-toggle" />
                Pin as admin template (visible to all teachers)
              </label>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button data-testid="qp-new-format-cancel" variant="outline" onClick={() => setShowFormatUpload(false)}>Cancel</Button>
              <Button data-testid="qp-new-format-submit" onClick={handleUploadFormat} disabled={uploadingFormat}
                      className="bg-orange-600 hover:bg-orange-700 text-white">
                {uploadingFormat ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing…</> : <><Upload className="w-4 h-4 mr-2" /> Upload & Parse</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionPaperBuilder;
